import { CommonModule } from '@angular/common'
import { Component, booleanAttribute, computed, inject, input, signal } from '@angular/core'

import { Store } from '@ngrx/store'

import { Point } from '@flowx/designer/modules/bpmn-editor/dictionary/geometry.types'

import { DragDirective } from '../../../../common/directives/drag/drag.directive'
import { DragType, Position } from '../../../../common/directives/drag/drag.models'
import { AiIconsModule } from '../../../../common/icons/ai-icons.module'
import { AiActions } from '../../../../store/ai.actions'
import { AiSelectors } from '../../../../store/ai.selectors'

@Component({
  selector: 'flx-ai-fab',
  templateUrl: './fab.component.html',
  styleUrls: ['./fab.component.scss'],
  host: {
    'class': 'fixed',
    '[style.zIndex]': 'boostZIndex() ? 2002 : 1005',
  },
  imports: [CommonModule, AiIconsModule, DragDirective],
})
export class AiFabComponent {
  #store = inject(Store)

  #fabPositionState = computed(() => {
    const fabPosition = this.#store.selectSignal(AiSelectors.fabPosition)()
    return {
      position: signal(fabPosition),
    }
  })
  #isFabMoved = false

  readonly boostZIndex = input(false, { transform: booleanAttribute })

  showFab = this.#store.selectSignal(AiSelectors.isMinimized)

  fabStyle = computed(() => {
    const position = this.#fabPositionState().position()
    return this.#convertPositionToNgStyle(position)
  })

  readonly DragType = DragType
  readonly moveTolerance = 2
  readonly dragPadding = { top: 80, right: 24, bottom: 80, left: 65 }

  showWindow(): void {
    if (!this.#isFabMoved) {
      this.#store.dispatch(AiActions.enterCommandCenter())
    }
  }

  onDragMove(event: { to: Position; delta: Point }): void {
    const { to, delta } = event

    if (delta) {
      this.#setFabMoved(delta)
      this.#fabPositionState().position.set(to)
    }
  }

  onDragEnd(event: { to: Position; delta: Point }): void {
    const { to, delta } = event

    if (delta) {
      this.#setFabMoved(delta)
      this.#store.dispatch(AiActions.fabMoved({ position: to }))
    }
  }

  #convertPositionToNgStyle(position: Position): Record<string, string> {
    return Object.entries(position).reduce(
      (style, [key, value]) => {
        style[key] = `${value}px`
        return style
      },
      {} as Record<string, string>
    )
  }

  #setFabMoved(delta: Point): void {
    this.#isFabMoved = this.#getIsFabMoved(delta)
  }

  #getIsFabMoved(delta: Point): boolean {
    return Math.abs(delta.x) > this.moveTolerance || Math.abs(delta.y) > this.moveTolerance
  }
}
